import type { MailThreadFilter } from '@fos/shared/contracts/mail';
import { MAIL_THREAD_FILTER_LABELS } from '@fos/shared/labels';
import { FlaskConical, PenLine } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState } from '../../../ui/States';
import { MobileFab, SEGMENTED_TOUCH, TOUCH_BUTTON, useIsPhone } from '../../mobile';
import { Composer } from '../components/Composer';
import { MailboxBanners, MailboxStatus } from '../components/MailboxStatus';
import { SimulateIncomingDialog } from '../components/SimulateIncomingDialog';
import { ThreadList } from '../components/ThreadList';
import { useMailStatus } from '../hooks';

/**
 * Poczta (`/poczta?widok=do-przypisania`): wszystkie wątki z klientem albo kolejka „Do przypisania” (nierozpoznany
 * i niejednoznaczny nadawca), stan skrzynek i „Synchronizuj teraz”, „Napisz”, narzędzie dev „Symuluj przychodzącą
 * wiadomość”. Widok bez projektu w Figmie (wzorce list i Inboxu; IMPLEMENTATION_STATUS: czeka na projekt).
 */
export function MailPage() {
  const access = useAccess();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter: MailThreadFilter = params.get('widok') === 'do-przypisania' ? 'unassigned' : 'all';
  const status = useMailStatus(access.can('inbox'));
  const [composeOpen, setComposeOpen] = useState(false);
  const [simulateOpen, setSimulateOpen] = useState(false);
  const canWrite = access.can('inbox', 'edit');
  const phone = useIsPhone();

  if (!access.can('inbox')) {
    return (
      <Page>
        <Section>
          <EmptyState
            title="Brak dostępu do poczty"
            description="Poczta należy do modułu Inbox. Poproś administratora o dostęp."
          />
        </Section>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Poczta"
        meta={<MailboxStatus compact banners={false} />}
        actions={
          <>
            {status.data?.canSimulate && (
              <Button
                variant="ghost"
                icon={FlaskConical}
                onClick={() => setSimulateOpen(true)}
                className={TOUCH_BUTTON}
              >
                Symuluj wiadomość
              </Button>
            )}
            {canWrite && (
              <Button variant="primary" icon={PenLine} onClick={() => setComposeOpen(true)} className="max-md:hidden">
                Napisz
              </Button>
            )}
          </>
        }
      />
      <MailboxBanners className="mt-4" />
      <Segmented<MailThreadFilter>
        aria-label="Widok"
        className={`mt-6 ${SEGMENTED_TOUCH}`}
        value={filter}
        onValueChange={(v) => setParams(v === 'unassigned' ? { widok: 'do-przypisania' } : {}, { replace: true })}
        options={(['all', 'unassigned'] as const).map((v) => ({ value: v, label: MAIL_THREAD_FILTER_LABELS[v] }))}
      />
      <Section className="mt-4">
        <ThreadList
          input={{ filter }}
          showClient
          onOpen={(id) => navigate(`/poczta/${id}`)}
          empty={
            filter === 'unassigned'
              ? { title: 'Wszystkie wiadomości mają klienta.' }
              : { title: 'Brak wiadomości.', description: 'Maile pojawią się tutaj po synchronizacji skrzynki.' }
          }
        />
      </Section>
      {canWrite && phone && (
        <MobileFab icon={PenLine} onClick={() => setComposeOpen(true)}>
          Napisz
        </MobileFab>
      )}
      {canWrite && <Composer open={composeOpen} onOpenChange={setComposeOpen} title="Nowa wiadomość" />}
      {status.data?.canSimulate && (
        <SimulateIncomingDialog
          open={simulateOpen}
          onOpenChange={setSimulateOpen}
          onCreated={(threadId) => navigate(`/poczta/${threadId}`)}
        />
      )}
    </Page>
  );
}
